/**
 * Central switches for development-only UI and debug behavior.
 *
 * - `IS_DEV_BUILD` / `isDevRuntime()` — Vite dev server & dev builds (tree-shaken in prod).
 * - `DEV_PANELS` — per-section toggles in the DEV drawer; flip one flag to validate in isolation.
 * - `devQuery` — URL flags that work in any build (e.g. `?pwa` on a preview deploy).
 * - `devEnv` — `.env.local` helpers documented in `.env.example`.
 */

export const IS_DEV_BUILD = import.meta.env.DEV;

/** Temporary: hide DEV FAB/drawer/panels for screenshots. Flip back to false when done. */
export const HIDE_DEV_UI = false;

export type DevPanelId =
  | 'environment'
  | 'firestoreReads'
  | 'auth'
  | 'supportModal'
  | 'matchHistory'
  | 'localMatch'
  | 'sharedMatch'
  | 'riftboundle'
  | 'teams'
  | 'scanner'
  | 'scannerOverlays'
  | 'phoneGestures';

/** Per-section visibility. Defaults follow the dev build; override here to validate in isolation. */
export const DEV_PANELS: Record<DevPanelId, boolean> = {
  environment: IS_DEV_BUILD,
  firestoreReads: IS_DEV_BUILD,
  auth: IS_DEV_BUILD,
  supportModal: IS_DEV_BUILD,
  matchHistory: IS_DEV_BUILD,
  localMatch: IS_DEV_BUILD,
  sharedMatch: IS_DEV_BUILD,
  riftboundle: IS_DEV_BUILD,
  teams: IS_DEV_BUILD,
  scanner: IS_DEV_BUILD,
  scannerOverlays: IS_DEV_BUILD,
  phoneGestures: IS_DEV_BUILD,
};

export function isDevPanelEnabled(id: DevPanelId): boolean {
  return !HIDE_DEV_UI && DEV_PANELS[id];
}

export function isDevRuntime(): boolean {
  return IS_DEV_BUILD && !HIDE_DEV_UI;
}

/**
 * Stacking above app modals/sheets (card sheet 1400, deck dialog 1480, edge arrows 10032).
 * Used for the DEV FAB and drawer portal.
 */
export const DEV_CONTROLS_Z = 10100;

export function hasDevQueryFlag(flag: string): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has(flag);
}

/** Query-string overrides — intentionally work on production URLs for preview/testing. */
export const devQuery = {
  pwaPreview: () => hasDevQueryFlag('pwa'),
} as const;

/** Vite env-backed debug helpers (see `.env.example`). */
export const devEnv = {
  debugFirestoreWrites: import.meta.env.VITE_DEBUG_FIRESTORE_WRITES === 'true',
  useFirebaseEmulators: import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true',
  debugUserEmail: import.meta.env.VITE_DEBUG_USER_EMAIL as string | undefined,
  debugUserPassword: import.meta.env.VITE_DEBUG_USER_PASSWORD as string | undefined,
  hasDebugSignIn(): boolean {
    return IS_DEV_BUILD && Boolean(this.debugUserEmail && this.debugUserPassword);
  },
} as const;
